import { ToastContext } from "context/ToastContext";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { useContext, useState } from "react";
import {
  useCreateClinicCopayRuleMutation,
  useDeleteClinicCopayRuleMutation,
  useGetClinicCopayRulesQuery,
  useUpdateClinicCopayRuleMutation,
} from "store/queries/clinicCopayRules";
import styled from "styled-components";
import {
  IClinicCopayRule,
  IClinicCopayRuleSaveRequest,
} from "types/ClinicCopayRule/ClinicCopayRule";

import { ClinicCopayRuleDialog } from "./ClinicCopayRuleDialog";
import {
  CLINIC_COPAY_INTRO,
  CLINIC_COPAY_RULES,
  KIND_LABELS,
  amountInWords,
} from "./clinicCopayRuleWording";
import { MODALITY_LABELS } from "./serviceTypeRuleWording";

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    vertical-align: top;
  }
`;

const keyOf = (rule: IClinicCopayRule) =>
  `${rule.payerId}.${rule.procedureGroup}`;

const payerInWords = (rule: IClinicCopayRule) =>
  rule.payerName ?? `Payer ${rule.payerId}`;

const toBody = (rule: IClinicCopayRule): IClinicCopayRuleSaveRequest => ({
  payerId: rule.payerId,
  procedureGroup: rule.procedureGroup,
  kind: rule.kind,
  fixedAmountInCents: rule.fixedAmountInCents,
  enabled: rule.enabled,
});

/**
 * CLE-3050 — Eligibility › Clinic copay rules: per payer and CPT group, the extra ("add-on") copay a
 * clinic visit is charged on top of the office copay when a scan or test is done at it. Global —
 * every clinic uses the same rules.
 *
 * At the top, what the rules are and how they combine (one add-on copay per visit, the highest of
 * the matching rules). Below, one table with an Add / Edit dialog, an Enabled switch and Remove with
 * a confirm. While nothing is saved the table shows the BUILT-IN rules, with a banner; the API
 * writes them all on the first save, so editing one never drops the rest. Mirrors the Copay list
 * tab. The page hides this tab when the API has no such route (404).
 */
export function ClinicCopayRulesTab() {
  const toast = useContext(ToastContext);
  const { data, isLoading, isError } = useGetClinicCopayRulesQuery();
  const [createRule, { isLoading: creating }] =
    useCreateClinicCopayRuleMutation();
  const [updateRule, { isLoading: updating }] =
    useUpdateClinicCopayRuleMutation();
  const [deleteRule] = useDeleteClinicCopayRuleMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<IClinicCopayRule | null>(null);

  const rules = data?.rows ?? [];
  const blockGroups = data?.blockGroups ?? [];

  const fail = (summary: string, error: any, fallback: string) =>
    toast?.current?.show({
      severity: "error",
      summary,
      detail: error?.data?.message || error?.message || fallback,
    });

  const handleSave = async (body: IClinicCopayRuleSaveRequest) => {
    try {
      if (editing) {
        await updateRule({
          payerId: editing.payerId,
          procedureGroup: editing.procedureGroup,
          body,
        }).unwrap();
      } else {
        await createRule(body).unwrap();
      }
      setDialogOpen(false);
      toast?.current?.show({
        severity: "success",
        summary: "Rule saved",
        detail: "The next eligibility check and estimate use it.",
      });
    } catch (error: any) {
      fail("Not saved", error, "The rule could not be saved.");
    }
  };

  const toggle = async (rule: IClinicCopayRule, enabled: boolean) => {
    try {
      await updateRule({
        payerId: rule.payerId,
        procedureGroup: rule.procedureGroup,
        body: { ...toBody(rule), enabled },
      }).unwrap();
    } catch (error: any) {
      fail("Not saved", error, "The rule could not be switched.");
    }
  };

  const remove = async (rule: IClinicCopayRule) => {
    // eslint-disable-next-line no-alert
    const sure = window.confirm(
      `Remove the ${
        MODALITY_LABELS[rule.procedureGroup]
      } rule for ${payerInWords(
        rule
      )}? Its lines go back to their usual copay. To pause it instead, switch it off.`
    );
    if (!sure) return;
    try {
      await deleteRule({
        payerId: rule.payerId,
        procedureGroup: rule.procedureGroup,
      }).unwrap();
    } catch (error: any) {
      fail("Not removed", error, "The rule could not be removed.");
    }
  };

  const openEditor = (rule: IClinicCopayRule | null) => {
    setEditing(rule);
    setDialogOpen(true);
  };

  const payerBody = (rule: IClinicCopayRule) => (
    <span data-testid={`clinicCopay.row.${keyOf(rule)}.payer`}>
      {payerInWords(rule)}
    </span>
  );

  const groupBody = (rule: IClinicCopayRule) => (
    <span data-testid={`clinicCopay.row.${keyOf(rule)}.group`}>
      {MODALITY_LABELS[rule.procedureGroup] ?? rule.procedureGroup}
    </span>
  );

  const kindBody = (rule: IClinicCopayRule) => (
    <span data-testid={`clinicCopay.row.${keyOf(rule)}.kind`}>
      {KIND_LABELS[rule.kind] ?? rule.kind}
    </span>
  );

  const amountBody = (rule: IClinicCopayRule) => (
    <span data-testid={`clinicCopay.row.${keyOf(rule)}.amount`}>
      {amountInWords(rule)}
    </span>
  );

  const enabledBody = (rule: IClinicCopayRule) => (
    <InputSwitch
      checked={rule.enabled}
      aria-label={`${rule.enabled ? "Switch off" : "Switch on"} ${
        MODALITY_LABELS[rule.procedureGroup]
      } for ${payerInWords(rule)}`}
      data-testid={`clinicCopay.row.${keyOf(rule)}.enabled`}
      disabled={updating}
      onChange={(event) => toggle(rule, !!event.value)}
    />
  );

  const actionsBody = (rule: IClinicCopayRule) => (
    <div className="flex gap-1 justify-content-end">
      <Button
        type="button"
        icon="pi pi-pencil"
        className="p-button-text p-button-sm"
        aria-label={`Edit ${
          MODALITY_LABELS[rule.procedureGroup]
        } for ${payerInWords(rule)}`}
        data-testid={`clinicCopay.row.${keyOf(rule)}.edit`}
        onClick={() => openEditor(rule)}
      />
      <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-text p-button-sm p-button-danger"
        aria-label={`Remove ${
          MODALITY_LABELS[rule.procedureGroup]
        } for ${payerInWords(rule)}`}
        data-testid={`clinicCopay.row.${keyOf(rule)}.remove`}
        onClick={() => remove(rule)}
      />
    </div>
  );

  return (
    <div className="flex flex-column gap-3" data-testid="clinicCopayRulesTab">
      <Surface>
        <p className="mt-0 mb-2" data-testid="clinicCopay.intro">
          {CLINIC_COPAY_INTRO}
        </p>
        <ul className="m-0 pl-4" data-testid="clinicCopay.rules">
          {CLINIC_COPAY_RULES.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </Surface>

      <Surface>
        <div className="flex flex-wrap gap-2 mb-2 align-items-end">
          <Button
            type="button"
            icon="pi pi-plus"
            label="Add rule"
            className="p-button-sm"
            data-testid="clinicCopay.add"
            onClick={() => openEditor(null)}
          />
        </div>

        {isLoading && (
          <div className="flex justify-content-center p-3">
            <ProgressSpinner
              style={{ width: "2rem", height: "2rem" }}
              data-testid="clinicCopay.loading"
            />
          </div>
        )}
        {isError && (
          <Message
            severity="error"
            text="The clinic copay rules could not be loaded."
            data-testid="clinicCopay.error"
          />
        )}
        {!isLoading && !isError && data?.builtIn && (
          <Message
            severity="info"
            className="mb-2 w-full justify-content-start"
            text="Showing the built-in rules: nothing is saved yet, and eligibility and pricing read these. The first save writes them all, then your change."
            data-testid="clinicCopay.builtIn"
          />
        )}
        {!isLoading && !isError && data && (
          <StyledDataTable
            value={rules}
            className="w-full"
            data-testid="clinicCopay.table"
            emptyMessage="No clinic copay rule."
          >
            <Column header="Payer" body={payerBody} />
            <Column header="CPT group" body={groupBody} />
            <Column header="Kind" body={kindBody} style={{ width: "9rem" }} />
            <Column header="Amount" body={amountBody} />
            <Column
              header="Enabled"
              body={enabledBody}
              style={{ width: "6rem" }}
            />
            <Column body={actionsBody} style={{ width: "6rem" }} />
          </StyledDataTable>
        )}
      </Surface>

      <ClinicCopayRuleDialog
        visible={dialogOpen}
        rule={editing}
        rules={rules}
        blockGroups={blockGroups}
        saving={creating || updating}
        onSave={handleSave}
        onHide={() => setDialogOpen(false)}
      />
    </div>
  );
}
